/**
 * An exact rational amount on bigint, with the subset of the decimal.js
 * interface the credit-note caps read. Every operation is exact, division
 * included; a value converts to a string or a number only when printed or
 * handed to a number-based rule. Its string form is decimal.js's for a
 * value with a finite decimal expansion (exponent notation below 1e-7 and
 * from 1e21), and the first 20 significant digits otherwise.
 */
const DECIMAL_PATTERN = /^([+-]?)(\d*)(?:\.(\d*))?(?:e([+-]?\d+))?$/i;
/** Significant digits of the string form of a value with no finite decimal expansion. */
const SIGNIFICANT_DIGITS = 20;
function gcd(a, b) {
    let x = a < 0n ? -a : a;
    let y = b < 0n ? -b : b;
    while (y !== 0n)
        [x, y] = [y, x % y];
    return x === 0n ? 1n : x;
}
function parse(text) {
    const match = DECIMAL_PATTERN.exec(text.trim());
    if (match === null || (match[2] === "" && (match[3] ?? "") === "")) {
        throw new Error(`Montant décimal invalide : « ${text} »`);
    }
    const [, sign, whole, decimals = "", exponentText] = match;
    const exponent = exponentText === undefined ? 0 : Number(exponentText);
    let numerator = BigInt(`${whole}${decimals}` || "0");
    let denominator = 1n;
    const scale = exponent - decimals.length;
    if (scale >= 0)
        numerator *= 10n ** BigInt(scale);
    else
        denominator = 10n ** BigInt(-scale);
    return [sign === "-" ? -numerator : numerator, denominator];
}
/** `numerator` × 10^`exponent`, written as decimal.js writes it: plain from 1e-6 to below 1e21, exponent notation otherwise. */
function written(negative, digits, exponent) {
    const sign = negative ? "-" : "";
    if (exponent <= -7 || exponent >= 21) {
        const mantissa = digits.length > 1 ? `${digits[0]}.${digits.slice(1)}` : digits;
        return `${sign}${mantissa}e${exponent < 0 ? "-" : "+"}${Math.abs(exponent)}`;
    }
    if (exponent < 0)
        return `${sign}0.${"0".repeat(-exponent - 1)}${digits}`;
    if (digits.length <= exponent + 1)
        return `${sign}${digits}${"0".repeat(exponent + 1 - digits.length)}`;
    return `${sign}${digits.slice(0, exponent + 1)}.${digits.slice(exponent + 1)}`;
}
export class ExactDecimal {
    /** In lowest terms; the denominator is positive. */
    numerator;
    denominator;
    constructor(value) {
        if (value instanceof ExactDecimal) {
            this.numerator = value.numerator;
            this.denominator = value.denominator;
            return;
        }
        if (typeof value === "bigint") {
            this.numerator = value;
            this.denominator = 1n;
            return;
        }
        if (typeof value === "number" && !Number.isFinite(value)) {
            throw new Error(`Montant décimal invalide : « ${value} »`);
        }
        const [numerator, denominator] = parse(String(value));
        const divisor = gcd(numerator, denominator);
        this.numerator = numerator / divisor;
        this.denominator = denominator / divisor;
    }
    /** `numerator` / `denominator`, reduced. */
    static fraction(numerator, denominator) {
        if (denominator === 0n)
            throw new Error("Division par zéro");
        const sign = denominator < 0n ? -1n : 1n;
        const divisor = gcd(numerator, denominator);
        const value = Object.create(ExactDecimal.prototype);
        value.numerator = (sign * numerator) / divisor;
        value.denominator = (sign * denominator) / divisor;
        return value;
    }
    static max(...values) {
        return values.map((value) => new ExactDecimal(value)).reduce((a, b) => (b.greaterThan(a) ? b : a));
    }
    static min(...values) {
        return values.map((value) => new ExactDecimal(value)).reduce((a, b) => (b.lessThan(a) ? b : a));
    }
    add(other) {
        const b = new ExactDecimal(other);
        return ExactDecimal.fraction(this.numerator * b.denominator + b.numerator * this.denominator, this.denominator * b.denominator);
    }
    sub(other) {
        const b = new ExactDecimal(other);
        return ExactDecimal.fraction(this.numerator * b.denominator - b.numerator * this.denominator, this.denominator * b.denominator);
    }
    mul(other) {
        const b = new ExactDecimal(other);
        return ExactDecimal.fraction(this.numerator * b.numerator, this.denominator * b.denominator);
    }
    div(other) {
        const b = new ExactDecimal(other);
        return ExactDecimal.fraction(this.numerator * b.denominator, this.denominator * b.numerator);
    }
    abs() {
        return this.numerator < 0n ? ExactDecimal.fraction(-this.numerator, this.denominator) : this;
    }
    /** Negative, zero or positive as this value is below, at or above `other`. */
    comparedTo(other) {
        const b = new ExactDecimal(other);
        const difference = this.numerator * b.denominator - b.numerator * this.denominator;
        return difference < 0n ? -1 : difference > 0n ? 1 : 0;
    }
    equals(other) {
        return this.comparedTo(other) === 0;
    }
    greaterThan(other) {
        return this.comparedTo(other) > 0;
    }
    greaterThanOrEqualTo(other) {
        return this.comparedTo(other) >= 0;
    }
    lessThan(other) {
        return this.comparedTo(other) < 0;
    }
    lessThanOrEqualTo(other) {
        return this.comparedTo(other) <= 0;
    }
    isZero() {
        return this.numerator === 0n;
    }
    isInteger() {
        return this.denominator === 1n;
    }
    /** Rounded to `places` decimals, an exact half away from zero. */
    toDecimalPlaces(places) {
        const scale = 10n ** BigInt(places);
        const scaled = this.numerator * scale;
        let quotient = scaled / this.denominator;
        const rest = scaled % this.denominator;
        const twiceRest = rest < 0n ? -2n * rest : 2n * rest;
        if (twiceRest >= this.denominator)
            quotient += scaled < 0n ? -1n : 1n;
        return ExactDecimal.fraction(quotient, scale);
    }
    floor() {
        const quotient = this.numerator / this.denominator;
        return ExactDecimal.fraction(this.numerator < 0n && this.numerator % this.denominator !== 0n ? quotient - 1n : quotient, 1n);
    }
    ceil() {
        const quotient = this.numerator / this.denominator;
        return ExactDecimal.fraction(this.numerator > 0n && this.numerator % this.denominator !== 0n ? quotient + 1n : quotient, 1n);
    }
    toString() {
        if (this.numerator === 0n)
            return "0";
        const negative = this.numerator < 0n;
        const magnitude = negative ? -this.numerator : this.numerator;
        let twos = 0;
        let fives = 0;
        let rest = this.denominator;
        while (rest % 2n === 0n)
            [rest, twos] = [rest / 2n, twos + 1];
        while (rest % 5n === 0n)
            [rest, fives] = [rest / 5n, fives + 1];
        let digits;
        let scale;
        if (rest === 1n) {
            scale = Math.max(twos, fives);
            digits = ((magnitude * 10n ** BigInt(scale)) / this.denominator).toString();
        }
        else {
            // The first SIGNIFICANT_DIGITS digits, the last rounded half away from zero.
            const integerDigits = (magnitude / this.denominator).toString().length;
            const leading = magnitude >= this.denominator ? integerDigits : -Math.floor(Math.log10(Number(this.denominator) / Number(magnitude)));
            scale = SIGNIFICANT_DIGITS - leading;
            const scaled = scale >= 0 ? magnitude * 10n ** BigInt(scale) : magnitude / 10n ** BigInt(-scale);
            let quotient = scaled / this.denominator;
            if (2n * (scaled % this.denominator) >= this.denominator)
                quotient += 1n;
            digits = quotient.toString();
        }
        const trimmed = digits.replace(/0+$/, "");
        const exponent = digits.length - 1 - scale;
        return written(negative, trimmed === "" ? "0" : trimmed, exponent);
    }
    toNumber() {
        return Number(this.toString());
    }
}
//# sourceMappingURL=exact-decimal.js.map